import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { deposerPiece, useActionsCourrier, useConseillers, useCourrier, type CourrierSaisie } from '@/api/hooks';
import { LIBELLE_PRIORITE, LIBELLE_TYPE_DOCUMENT, type CourrierEntrant, type Priorite, type TypeDocumentCourrier } from '@/api/types';
import { Chargement, PageHeader } from '@/components/common';
import { ChampFichier, messageErreurFichier } from '@/components/pieces-jointes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';

interface Valeurs {
  dateReception: string;
  dateCourrier: string;
  expediteur: string;
  referenceExpediteur: string;
  objet: string;
  typeDocument: TypeDocumentCourrier;
  priorite: Priorite;
  confidentiel: boolean;
  motsCles: string;
  conseillerId: string;
}

const auj = () => new Date().toISOString().slice(0, 10);
const depuis = (c: CourrierEntrant): Valeurs => ({
  dateReception: c.dateReception,
  dateCourrier: c.dateCourrier ?? '',
  expediteur: c.expediteur,
  referenceExpediteur: c.referenceExpediteur ?? '',
  objet: c.objet,
  typeDocument: c.typeDocument,
  priorite: c.priorite,
  confidentiel: c.confidentiel,
  motsCles: (c.motsCles ?? []).join(', '),
  conseillerId: c.conseillerId ?? '',
});

/** Enregistrement d'un courrier entrant par l'assistante du DG (cachet « Courrier arrivée » apposé par le système). */
export function CourrierFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const existant = useCourrier(id);
  const conseillers = useConseillers();
  const actions = useActionsCourrier();
  // Document principal numérisé (PDF ou image), obligatoire : joint juste après l'enregistrement.
  const [fichier, setFichier] = useState<File | null>(null);
  const form = useForm<Valeurs>({ defaultValues: { dateReception: auj(), dateCourrier: '', expediteur: '', referenceExpediteur: '', objet: '', typeDocument: 'LETTRE', priorite: 'NORMALE', confidentiel: false, motsCles: '', conseillerId: '' } });

  useEffect(() => {
    if (existant.data) form.reset(depuis(existant.data.courrier));
  }, [existant.data, form]);

  const enregistrer = form.handleSubmit(
    async (v) => {
      const corps: CourrierSaisie = {
        dateReception: v.dateReception,
        dateCourrier: v.dateCourrier || undefined,
        expediteur: v.expediteur.trim(),
        referenceExpediteur: v.referenceExpediteur.trim() || undefined,
        objet: v.objet.trim(),
        typeDocument: v.typeDocument,
        priorite: v.priorite,
        confidentiel: v.confidentiel,
        motsCles: v.motsCles.split(',').map((x) => x.trim()).filter(Boolean),
        conseillerId: v.conseillerId || undefined,
      };
      if (!id && !fichier) return toast.error('Le document principal numérisé (PDF ou image) est obligatoire.');
      let r;
      try {
        r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
      } catch (e) {
        return toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
      }
      if (!id && fichier) {
        try {
          await deposerPiece('CE', r.courrier.id, fichier, 'DOCUMENT_PRINCIPAL');
          toast.success(`Courrier ${r.courrier.numero} enregistré avec son document numérisé.`);
        } catch (e) {
          toast.error(`Courrier ${r.courrier.numero} enregistré, mais le document n'a pas pu être joint : ${messageErreurFichier(e)} Joignez-le depuis la page du courrier.`);
        }
      } else toast.success('Courrier modifié.');
      navigate(`/courrier/${r.courrier.id}`);
    },
    () => toast.error("Renseignez la date de réception, l'expéditeur et l'objet."),
  );

  if (id && existant.isLoading) return <Chargement />;

  return (
    <form onSubmit={enregistrer} className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/courrier/${id}` : '/courrier'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Modifier le courrier ${existant.data?.courrier.numero ?? ''}` : 'Enregistrer un courrier entrant'}
        sousTitre="Le numéro CE/{année}/{séquence} est attribué automatiquement ; le cachet « Courrier arrivée » porte le numéro et la date de réception."
        actions={
          <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
            {id ? 'Enregistrer' : 'Enregistrer le courrier'}
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <CardTitle>Courrier</CardTitle>
          <CardDescription>La priorité est proposée par l'assistante et confirmée par le DG.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="reception">Date de réception</Label>
            <Input id="reception" type="date" {...form.register('dateReception', { required: true })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateCourrier">Date du courrier</Label>
            <Input id="dateCourrier" type="date" {...form.register('dateCourrier')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="expediteur">Expéditeur</Label>
            <Input id="expediteur" {...form.register('expediteur', { required: true, minLength: 2 })} placeholder="Tribunal de grande instance de Bunia" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reference">Référence de l'expéditeur</Label>
            <Input id="reference" {...form.register('referenceExpediteur')} placeholder="TGI/BUN/2026/418" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="objet">Objet</Label>
            <Input id="objet" {...form.register('objet', { required: true, minLength: 5 })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="type">Type de document</Label>
            <SelectNatif id="type" {...form.register('typeDocument')}>
              {(Object.keys(LIBELLE_TYPE_DOCUMENT) as TypeDocumentCourrier[]).map((t) => (
                <option key={t} value={t}>
                  {LIBELLE_TYPE_DOCUMENT[t]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priorite">Priorité</Label>
            <SelectNatif id="priorite" {...form.register('priorite')}>
              {(Object.keys(LIBELLE_PRIORITE) as Priorite[]).map((p) => (
                <option key={p} value={p}>
                  {LIBELLE_PRIORITE[p]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="conseiller">Conseiller technique compétent</Label>
            <SelectNatif id="conseiller" {...form.register('conseillerId')}>
              <option value="">À désigner</option>
              {(conseillers.data ?? []).map((c) => (
                <option key={c.agentId} value={c.agentId}>
                  {c.nom} · {c.fonction}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mots">Mots-clés (séparés par des virgules)</Label>
            <Input id="mots" {...form.register('motsCles')} placeholder="audiences, Bunia" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="document">Document principal numérisé (PDF ou image)</Label>
            {id ? <div className="text-sm text-muted-foreground">Les pièces se gèrent depuis la page du courrier.</div> : <ChampFichier id="document" valeur={fichier} onChange={setFichier} obligatoire />}
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <Controller control={form.control} name="confidentiel" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
            Confidentiel (traité par le DG seul)
          </label>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={id ? `/courrier/${id}` : '/courrier'}>Abandonner</Link>
        </Button>
        <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
          {id ? 'Enregistrer' : 'Enregistrer le courrier'}
        </Button>
      </div>
    </form>
  );
}
